'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Brain, Menu, X, User, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import logo from '@/assets/logo.svg'
import Image from 'next/image'
import { useAuthContext } from '@/lib/context/auth-context'
import { useLogout } from '@/hooks/use-auth'
import { useProfile } from '@/hooks/use-settings'

const navItems = [
  { label: 'Home', href: '/#hero' },
  { label: 'Privacy', href: '/#security-privacy' },
  { label: 'About', href: '/#about' },
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Support', href: '/#support' },
]

export function SiteHeader() {
  const [scrolled, setScrolled] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const { isAuthenticated, user } = useAuthContext()
  const logoutMutation = useLogout()

  // Only fetch profile data if user is authenticated
  const { data: profileData } = useProfile({ enabled: isAuthenticated })
  const profile = profileData?.data
  
  const handleLogout = () => {
    logoutMutation.mutate()
  }

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close mobile menu on navigation
  React.useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Active anchor highlighting (hash-based)
  const [hash, setHash] = React.useState('')
  React.useEffect(() => {
    const updateHash = () => setHash(window.location.hash)
    updateHash()
    window.addEventListener('hashchange', updateHash)
    return () => window.removeEventListener('hashchange', updateHash)
  }, [])

  // Prevent body scroll when mobile drawer is open
  React.useEffect(() => {
    if (open) {
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prevOverflow
      }
    }
  }, [open])

  return (
    <header
      className={cn(
        'py-1 sticky top-0 z-50 w-full border-b transition-colors duration-300',
        scrolled
          ? 'bg-background/95 supports-[backdrop-filter]:bg-background/70 backdrop-blur border-border'
          : 'bg-transparent border-transparent'
      )}
    >
      <div className='container mx-auto flex items-center justify-between px-4'>
        {/* Brand */}
        <Link href='/' className='flex items-center space-x-2'>
          <Image
            src={logo}
            alt='Logo'
            width={190}
            height={75}
            className='w-[160px] h-auto md:w-[170px] md:h-auto xl:w-[190px] xl:h-auto'
          />
        </Link>

        {/* Desktop nav */}
        <nav className='hidden lg:flex items-center gap-6'>
          {navItems.map((item) => {
            const [path, hashTarget] = item.href.split('#')
            const targetPath = path === '' ? '/' : path
            const targetHash = hashTarget ? `#${hashTarget}` : ''
            const isHashLink = item.href.includes('#')
            const active = isHashLink
              ? pathname === targetPath && hash === targetHash
              : pathname === targetPath
            const linkClassName = cn(
              'text-sm font-medium text-muted-foreground hover:text-foreground transition-colors relative',
              active && 'active text-foreground'
            )
            const underline = (
              <span
                className={cn(
                  'absolute left-0 -bottom-1 h-0.5 w-full scale-x-0 bg-[hsl(var(--primary-blue))] transition-transform',
                  active ? 'scale-x-100' : 'hover:scale-x-100'
                )}
              />
            )
            return isHashLink ? (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setHash(targetHash)}
                className={linkClassName}
              >
                {item.label}
                {underline}
              </a>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className={linkClassName}
              >
                {item.label}
                {underline}
              </Link>
            )
          })}
        </nav>

        {/* Desktop CTAs */}
        <div className='hidden lg:flex items-center gap-3'>
          {isAuthenticated ? (
            <>
              <Button variant='ghost' size='sm' asChild>
                <Link href='/dashboard' className='flex items-center gap-1'>
                  <User className='h-4 w-4' />
                  {profile?.first_name || user?.first_name || 'Dashboard'}
                </Link>
              </Button>
              <Button 
                variant='outline' 
                size='sm' 
                onClick={handleLogout}
                className='flex items-center gap-1'
              >
                <LogOut className='h-4 w-4' />
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant='ghost' size='sm' asChild>
                <Link href='/login'>Login</Link>
              </Button>
              <Button variant='hero' size='sm' asChild>
                <Link href='/signup'>Get Started</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className='lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground'
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
        </button>
      </div>

      {/* Mobile backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/30 z-40 lg:hidden transition-opacity',
          open
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
        onClick={() => setOpen(false)}
      />

      {/* Mobile left drawer */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-full h-[100dvh] bg-background border-r border-border lg:hidden',
          'transform transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-hidden={!open}
      >
        <div className='h-full flex flex-col'>
          <div className='px-4 py-3 border-b border-border flex items-center justify-between'>
            <Link
              href='/'
              className='flex items-center space-x-2'
              onClick={() => setOpen(false)}
            >
              <Image
                src={logo}
                alt='Logo'
                width={70}
                height={70}
                className='w-[140px] h-auto md:w-[150px] md:h-auto lg:w-[160px] lg:h-auto xl:w-[170px] xl:h-auto'
              />
            </Link>
            <button
              className='inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground'
              aria-label='Close menu'
              onClick={() => setOpen(false)}
            >
              <X className='h-5 w-5' />
            </button>
          </div>

          <nav className='px-4 py-3 space-y-1 flex-1 overflow-y-auto'>
            {navItems.map((item) => {
              const [, hashTarget] = item.href.split('#')
              const targetHash = hashTarget ? `#${hashTarget}` : ''
              const isHashLink = item.href.includes('#')
              const linkClassName =
                'block py-2 text-sm font-medium text-muted-foreground hover:text-foreground'
              return isHashLink ? (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => {
                    setHash(targetHash)
                    setOpen(false)
                  }}
                  className={linkClassName}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={linkClassName}
                >
                  {item.label}
                </Link>
              )
            })}
          </nav>

          <div className='px-4 py-3 border-t border-border flex flex-col gap-3'>
            {isAuthenticated ? (
              <>
                <Button variant='outline' size='sm' asChild className='w-full'>
                  <Link href='/dashboard' onClick={() => setOpen(false)} className='flex items-center justify-center gap-2'>
                    <User className='h-4 w-4' />
                    {user?.first_name || 'Dashboard'}
                  </Link>
                </Button>
                <Button 
                  variant='ghost' 
                  size='sm' 
                  onClick={() => {
                    handleLogout()
                    setOpen(false)
                  }}
                  className='w-full flex items-center justify-center gap-2'
                >
                  <LogOut className='h-4 w-4' />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button variant='ghost' size='sm' asChild className='w-full'>
                  <Link href='/login' onClick={() => setOpen(false)}>
                    Login
                  </Link>
                </Button>
                <Button variant='hero' size='sm' asChild className='w-full'>
                  <Link href='/signup' onClick={() => setOpen(false)}>
                    Get Started
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </aside>
    </header>
  )
}

export default SiteHeader
